import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  crawlWebsiteApi,
  getDiscoveredPagesApi,
  selectPagesForAnalysisApi,
  runSeoAnalysisApi
} from '../apis/projects.api';
import { getCreditBalanceApi, checkSeoEligibilityApi } from '../apis/payments.api';
import { toast } from 'react-toastify';
import { useDarkMode } from '../hooks/useDarkMode';

/**
 * Website Crawler & SEO Analysis Component
 * 
 * This component handles the complete SEO analysis workflow:
 * 1. Crawl website to discover pages
 * 2. Display discovered pages
 * 3. Allow user to select pages for analysis
 * 4. Run comprehensive SEO analysis
 * 
 * Props:
 * - projectId: The project ID
 * - domainUrl: The website domain URL
 * - onAnalysisComplete: Callback when analysis is done
 */
const WebsiteSeoAnalyzer = ({ projectId, domainUrl, onAnalysisComplete }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isDarkMode } = useDarkMode();
  const [currentStep, setCurrentStep] = useState(1); // 1: crawl, 2: select, 3: analyze, 4: results
  const [crawling, setCrawling] = useState(false);
  const [pages, setPages] = useState([]);
  const [selectedPageIds, setSelectedPageIds] = useState([]);
  const [analyzing, setAnalyzing] = useState(false);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [keyword, setKeyword] = useState('');
  const [location_, setLocation_] = useState('US');
  const [language, setLanguage] = useState('en');
  const [maxPages, setMaxPages] = useState(100);
  const [showPlanBanner, setShowPlanBanner] = useState(false);
  const [customPath, setCustomPath] = useState('');
  const [showCrawlChoice, setShowCrawlChoice] = useState(false);
  const [hasPreviousCrawl, setHasPreviousCrawl] = useState(false);
  const [userCredits, setUserCredits] = useState(null);
  const [trialEligible, setTrialEligible] = useState(false);
  const [showFreeTierModal, setShowFreeTierModal] = useState(false);
  const [hasSubscription, setHasSubscription] = useState(false);
  const [trialUsed, setTrialUsed] = useState(false);
  const [lastEligibilityCheck, setLastEligibilityCheck] = useState(0); // Prevent frequent checks

  // Load existing pages if available
  useEffect(() => {
    loadDiscoveredPages();
    loadUserCredits();
    checkTrialEligibility();
  }, [projectId]);

  // Refresh eligibility when page becomes visible (after payment redirect)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        // Only refresh if more than 5 seconds since last check (avoid rapid re-checks)
        const now = Date.now();
        if (now - lastEligibilityCheck > 5000) {
          console.log('[TRIAL] Page became visible, refreshing eligibility');
          checkTrialEligibility();
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [lastEligibilityCheck]);

  // Check trial eligibility
  const checkTrialEligibility = async () => {
    try {
      setLastEligibilityCheck(Date.now());
      const response = await checkSeoEligibilityApi({ project_id: projectId });
      const data = response.data;

      console.log('[TRIAL] Eligibility check result:', data);

      setHasSubscription(data.has_subscription || false);
      setTrialEligible(data.trial_eligible || false);
      setTrialUsed(data.trial_used || false);

      // Cache the result in sessionStorage for consistency during this session
      // IMPORTANT: Cache per USER, not per project - trial is one-time per user globally
      sessionStorage.setItem(
        'trial_status_user', // User-level cache, not project-specific
        JSON.stringify({
          has_subscription: data.has_subscription || false,
          trial_eligible: data.trial_eligible || false,
          trial_used: data.trial_used || false,
          timestamp: Date.now()
        })
      );

      console.log('[TRIAL] Trial eligibility updated:', data);
    } catch (error) {
      console.error('[TRIAL] Failed to check trial eligibility:', error);
      // Try to use cached value if available
      const cached = sessionStorage.getItem('trial_status_user'); // User-level cache
      if (cached) {
        try {
          const cachedData = JSON.parse(cached);
          console.log('[TRIAL] Using cached eligibility:', cachedData);
          setHasSubscription(cachedData.has_subscription || false);
          setTrialEligible(cachedData.trial_eligible || false);
          setTrialUsed(cachedData.trial_used || false);
        } catch (e) {
          console.error('[TRIAL] Failed to parse cached data:', e);
        }
      }
    }
  };

  // Load user credits from API
  const loadUserCredits = async () => {
    try {
      const response = await getCreditBalanceApi();
      const credits = response?.data?.remaining_credits || response?.data?.credits || 0;
      console.log('Loaded user credits from API:', credits);
      setUserCredits(credits);
    } catch (error) {
      console.error('Failed to load credits from API:', error);
      // Fallback to localStorage
      try {
        let credits = null;
        const directCredits = localStorage.getItem('credits');
        if (directCredits !== null && directCredits !== 'undefined' && directCredits !== 'null') {
          credits = parseInt(directCredits) || 0;
        }
        if (credits === null) {
          const account = localStorage.getItem('account') || localStorage.getItem('user');
          if (account && account !== 'undefined' && account !== 'null') {
            const parsed = JSON.parse(account);
            credits = parsed.credits || parsed.credit || parsed.remaining_credits || 0;
          }
        }
        console.log('Loaded user credits from localStorage (fallback):', credits);
        setUserCredits(credits !== null ? credits : 0);
      } catch (fallbackError) {
        console.error('Failed to load credits from localStorage:', fallbackError);
        setUserCredits(0);
      }
    }
  };

  const baseDomain = React.useMemo(() => {
    if (!domainUrl) return '';
    return domainUrl.endsWith('/') ? domainUrl.slice(0, -1) : domainUrl;
  }, [domainUrl]);

  const isMockDomain = React.useMemo(() => {
    return (baseDomain || '').toLowerCase().includes('novarsistech.in');
  }, [baseDomain]);

  // const handleAddCustomPage = (e) => {
  //   e.preventDefault();
  //   const trimmed = customPath.trim();
  //   if (!trimmed) {
  //     toast.warning('Please enter a path to search');
  //     return;
  //   }

  //   const normalizedPath = trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
  //   const fullUrl = `${baseDomain}${normalizedPath}`;

  //   // Avoid duplicates
  //   const exists = pages.some((p) => p.url === fullUrl);
  //   if (exists) {
  //     toast.info('This endpoint is already listed. Selecting it for you.');
  //     const existing = pages.find((p) => p.url === fullUrl);
  //     if (existing?.id) {
  //       setSelectedPageIds((prev) => (prev.includes(existing.id) ? prev : [...prev, existing.id]));
  //     }
  //     setCustomPath('');
  //     return;
  //   }

  //   const newId = `custom-${Date.now()}`;
  //   const newPage = { id: newId, url: fullUrl, isCustom: true };
  //   setPages((prev) => [...prev, newPage]);
  //   setSelectedPageIds((prev) => [...prev, newId]);
  //   setCustomPath('');
  //   toast.success('Added and selected your endpoint');
  // };

  // Filter pages live as user types
  const filteredPages = React.useMemo(() => {
    const query = customPath.trim().toLowerCase();
    if (!query) return pages;
    return pages.filter((p) => (p.url || '').toLowerCase().includes(query));
  }, [pages, customPath]);

  const loadDiscoveredPages = async () => {
    try {
      const response = await getDiscoveredPagesApi(projectId);
      if (response.data.pages && response.data.pages.length > 0) {
        setPages(response.data.pages);
        setHasPreviousCrawl(true);
        setShowCrawlChoice(true); // Show dialog asking user what to do
      }
    } catch (error) {
      // No pages discovered yet, stay on step 1
      setHasPreviousCrawl(false);
    }
  };

  // Handle user choice: continue with previous crawl
  const handleContinuePreviousCrawl = () => {
    setShowCrawlChoice(false);
    setCurrentStep(2); // Go to selection step
  };

  // Handle user choice: crawl again (start fresh)
  const handleCrawlAgain = () => {
    setShowCrawlChoice(false);
    setPages([]);
    setSelectedPageIds([]);
    setCurrentStep(1); // Go back to crawl step
  };

  // Step 1: Crawl Website
  const handleCrawl = async () => {
    setCrawling(true);
    const pages = maxPages === '' ? 100 : parseInt(maxPages);
    console.log('Starting crawl with:', { project_id: projectId, max_pages: pages, domainUrl });

    try {
      const response = await crawlWebsiteApi({
        project_id: projectId,
        max_pages: pages
      });

      console.log('Crawl response:', response);

      if (response && response.data) {
        // Backend returns pages in 'discovered_sample' field (first 50 URLs)
        // We need to fetch all pages from the API endpoint
        const totalFound = response.data.total_pages_found || response.data.total_pages || 0;

        console.log('Total pages found:', totalFound);

        // Fetch all discovered pages from the backend
        const pagesResponse = await getDiscoveredPagesApi(projectId);
        console.log('Fetched pages:', pagesResponse);

        const pagesData = pagesResponse?.data?.pages || pagesResponse?.data?.results || [];
        if (pagesData.length >= 0) {
          setPages(pagesData);
          toast.success(`✅ Found ${totalFound} pages on your website`);
          setCurrentStep(2);
        } else {
          toast.error('❌ Failed to fetch discovered pages');
        }
      } else {
        toast.error('❌ No data returned from crawl');
        console.error('Invalid response structure:', response);
      }
    } catch (error) {
      console.error('Crawl error details:', {
        message: error.message,
        response: error.response,
        status: error.response?.status,
        data: error.response?.data,
        error: error
      });

      const errorMessage = error.response?.data?.error
        || error.response?.data?.message
        || error.message
        || 'Failed to crawl website';

      toast.error(`❌ ${errorMessage}. Check console for details.`);
    } finally {
      setCrawling(false);
    }
  };

  // Step 2: Select/Deselect Pages
  const handleTogglePage = (pageId) => {
    setSelectedPageIds(prev => {
      const isSelected = prev.includes(pageId);

      if (!isSelected) {
        // Adding a new page - check trial restriction
        if (!hasSubscription && trialEligible && !trialUsed && prev.length >= 1) {
          setShowFreeTierModal(true);
          return prev; // Don't add more
        }
      }

      return isSelected
        ? prev.filter(id => id !== pageId)
        : [...prev, pageId];
    });
  };

  const handleSelectAll = () => {
    // Check trial restriction
    if (!hasSubscription && trialEligible && !trialUsed) {
      if (pages.length > 1) {
        setShowFreeTierModal(true);
        return;
      }
    }

    if (selectedPageIds.length === pages.length) {
      setSelectedPageIds([]);
    } else {
      setSelectedPageIds(pages.map(p => p.id));
    }
  };

  const handleConfirmSelection = async () => {
    if (selectedPageIds.length === 0) {
      toast.warning('Please select at least one page');
      return;
    }

    try {
      console.log('Selecting pages:', { project_id: projectId, page_ids: selectedPageIds });
      const response = await selectPagesForAnalysisApi({
        project_id: projectId,
        page_ids: selectedPageIds
      });
      console.log('Page selection response:', response);

      toast.success(`✅ ${selectedPageIds.length} pages marked for analysis`);
      setCurrentStep(3);
    } catch (error) {
      console.error('Page selection error:', {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message
      });

      if (error.response?.status === 401) {
        toast.error('❌ Session expired. Please login again.');
        // Don't navigate - let the axios interceptor handle it
      } else {
        toast.error('❌ Failed to confirm page selection');
      }
    }
  };

  // Step 3: Run SEO Analysis
  const calculateCost = () => {
    if (isMockDomain) return 0;
    const domainCost = 24;
    const pageCost = selectedPageIds.length * 20;
    return domainCost + pageCost;
  };

  // Direct analysis execution (used after trial purchase)
  const executeAnalysis = async () => {
    setAnalyzing(true);
    console.log('Starting SEO analysis with:', {
      project_id: projectId,
      selected_pages: selectedPageIds.length,
      keyword,
      location: location_,
      language
    });

    try {
      const response = await runSeoAnalysisApi({
        project_id: projectId,
        keyword: keyword || undefined,
        location: location_,
        language: language
      });

      console.log('Analysis response:', response);
      setAnalysisResult(response.data);

      const { credits_deducted, remaining_credits, run_id } = response.data;

      // Update local credits state
      setUserCredits(remaining_credits);

      toast.success(
        `✅ SEO Analysis Complete!\n💳 ${credits_deducted} credits used\n💰 ${remaining_credits} credits remaining`,
        { autoClose: 6000 }
      );

      setCurrentStep(4);

      // Callback to parent component
      if (onAnalysisComplete) {
        onAnalysisComplete(run_id);
      }
    } catch (error) {
      console.error('Analysis error:', {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message
      });

      const status = error.response?.status;
      const errorCode = error.response?.data?.error_code;

      if (status === 401) {
        toast.error('❌ Session expired. Please login again.');
        return;
      }

      if (status === 403 && (errorCode === 'NO_PLAN' || errorCode === 'NO_CREDITS')) {
        setShowPlanBanner(true);
        const needed = error.response?.data?.message || 'Not enough credits. Please buy a plan.';
        toast.error(`❌ ${needed}`);
        return;
      }

      if (errorCode === 'PLAN_EXPIRED') {
        setShowPlanBanner(true);
        toast.error('❌ Your plan has expired. Please renew to continue.');
        return;
      }

      toast.error('❌ Failed to run SEO analysis. Please try again.');
    } finally {
      setAnalyzing(false);
    }
  };

  const handleRunAnalysis = async () => {
    if (selectedPageIds.length === 0) {
      toast.warning('No pages selected for analysis');
      return;
    }

    // Check if user has zero credits or insufficient credits
    const requiredCredits = calculateCost();

    if (!hasSubscription && !isMockDomain && (userCredits === 0 || userCredits < requiredCredits)) {
      // If user is eligible for free tier, allow one-page analysis
      if (trialEligible && !trialUsed) {
        executeAnalysis();
        return;
      }

      // Otherwise show error
      if (userCredits === 0) {
        toast.error('❌ Your credits are zero. Please purchase a plan to analyze other websites.');
        setShowPlanBanner(true);
        return;
      }

      if (userCredits < requiredCredits) {
        toast.error(`❌ Insufficient credits. You need ${requiredCredits} credits but only have ${userCredits}. Please purchase more credits.`);
        setShowPlanBanner(true);
        return;
      }
    }

    // If checks pass, execute the analysis
    executeAnalysis();
  };

  return (
    <div className="website-seo-analyzer max-w-6xl mx-auto p-2 sm:p-6 ">
      {/* Trial Purchased Banner - Show when trial is purchased but not yet used */}
      {!hasSubscription && !trialEligible && !trialUsed && (
        <div className={`mb-6 p-4 rounded-xl border-2 ${isDarkMode
          ? 'bg-green-900/20 border-green-600/50 text-green-200'
          : 'bg-green-50 border-green-400 text-green-800'
          }`}>
          <div className="flex items-start gap-3">
            <span className="text-2xl">✅</span>
            <div className="flex-1">
              <h4 className="font-bold mb-1">Free Tier Activated</h4>
              <p className="text-sm">
                Free tier is active! You can run analysis for <strong>only 1 page once</strong>. Choose a page and click "Run Analysis" to get started.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Trial Available Banner */}
      {!hasSubscription && trialEligible && !trialUsed && (
        <div className={`mb-6 p-4 rounded-xl border-2 ${isDarkMode
          ? 'bg-yellow-900/20 border-yellow-600/50 text-yellow-200'
          : 'bg-yellow-50 border-yellow-400 text-yellow-800'
          }`}>
          <div className="flex items-start gap-3">
            <span className="text-2xl">💡</span>
            <div className="flex-1">
              <h4 className="font-bold mb-1">Free Tier Available</h4>
              <p className="text-sm">
                You can analyze <strong>1 page for free</strong> to test our service. Select a page and click "Run Analysis" to try it!
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Trial Used Banner - Show when trial has been used */}


      {/* Crawl Choice Dialog */}
      {showCrawlChoice && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className={`${isDarkMode ? 'bg-gray-900' : 'bg-white'} rounded-lg shadow-xl max-w-md w-full p-6 relative`}>
            {/* Close Button */}
            <button
              onClick={handleContinuePreviousCrawl}
              className={`absolute top-4 right-4 p-2 rounded-lg transition-colors ${isDarkMode
                ? 'hover:bg-gray-800 text-gray-400 hover:text-gray-200'
                : 'hover:bg-gray-100 text-gray-500 hover:text-gray-700'
                }`}
              title="Close"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            <h2 className={`text-2xl font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              Previous Crawl Found
            </h2>
            <p className={`mb-6 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              We found {pages.length} pages from your previous website crawl. What would you like to do?
            </p>

            <div className="space-y-3">
              <button
                onClick={handleContinuePreviousCrawl}
                className="w-full px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors"
              >
                Continue with Previous Results
              </button>
              <button
                onClick={handleCrawlAgain}
                className={`w-full px-4 py-3 font-medium rounded-lg transition-colors ${isDarkMode
                  ? 'bg-gray-700 hover:bg-gray-600 text-white'
                  : 'bg-gray-200 hover:bg-gray-300 text-gray-900'
                  }`}
              >
                Crawl Again (Start Fresh)
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Free Tier Restriction Modal */}
      {showFreeTierModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className={`${isDarkMode ? 'bg-gray-900' : 'bg-white'} rounded-lg shadow-xl max-w-md w-full p-6 relative`}>
            <button
              onClick={() => setShowFreeTierModal(false)}
              className={`absolute top-4 right-4 p-2 rounded-lg transition-colors ${isDarkMode
                ? 'hover:bg-gray-800 text-gray-400 hover:text-gray-200'
                : 'hover:bg-gray-100 text-gray-500 hover:text-gray-700'
                }`}
              title="Close"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            <h2 className={`text-2xl font-bold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              Free/Demo Tier Limit
            </h2>
            <p className={`mb-6 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              In the free/demo tier you can run SEO analysis for <strong>only one page</strong>. To analyze more pages, please purchase a plan.
            </p>

            <div className="space-y-3">
              <button
                onClick={() => setShowFreeTierModal(false)}
                className={`w-full px-4 py-3 font-medium rounded-lg transition-colors ${isDarkMode
                  ? 'bg-gray-700 hover:bg-gray-600 text-white'
                  : 'bg-gray-200 hover:bg-gray-300 text-gray-900'
                  }`}
              >
                Continue with 1 Page (Free/Demo)
              </button>
              <button
                onClick={() => {
                  setShowFreeTierModal(false);
                  navigate('/dashboard/pricing');
                }}
                className="w-full px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors"
              >
                Purchase a Plan
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Progress Steps */}
      <div className="mb-8">
        <div className="w-full">
          {/* Steps */}
          <div className="grid grid-cols-4 items-center">
            {[1, 2, 3, 4].map((step, index) => (
              <div key={step} className="relative flex flex-col items-center">
                {/* Circle */}
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center font-bold z-10 ${currentStep >= step
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-200 text-gray-600'
                    }`}
                >
                  {step}
                </div>

                {/* Line */}
                {index < 3 && (
                  <div
                    className={`absolute top-5 left-1/2 w-full h-1 ${currentStep > step ? 'bg-blue-600' : 'bg-gray-200'
                      }`}
                  />
                )}
              </div>
            ))}
          </div>

          {/* Labels */}
          <div
            className={`grid grid-cols-4 mt-3 text-sm text-center ${isDarkMode ? 'text-gray-300' : 'text-gray-600'
              }`}
          >
            <span>Crawl</span>
            <span>Select</span>
            <span>Analyze</span>
            <span>Results</span>
          </div>
        </div>

      </div>

      {/* Step 1: Crawl Website */}
      {currentStep === 1 && (
        <div className="bg-white p-6 rounded-lg shadow-md">
          <h2 className="text-2xl font-bold mb-4">Step 1: Discover Pages</h2>
          <p className="text-gray-600 mb-6">
            Crawl your website to discover all available pages for SEO analysis.
          </p>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Website URL
              </label>
              <input
                type="text"
                value={domainUrl}
                disabled
                className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Max Pages to Discover
              </label>
              <input
                type="number"
                value={maxPages}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val === '') {
                    setMaxPages('');
                  } else {
                    const num = parseInt(val) || 1;
                    setMaxPages(Math.max(1, Math.min(num, 1000)));
                  }
                }}
                min="1"
                max="1000"
                placeholder="Enter number between 1-1000"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
              <p className="text-xs text-gray-500 mt-1">
                Recommended: 100-500 pages
              </p>
              {maxPages === '' && (
                <p className="text-xs text-red-500 mt-1">
                  ⚠️ Please enter a number between 1-1000
                </p>
              )}
            </div>

            <button
              onClick={handleCrawl}
              disabled={crawling || maxPages === ''}
              className={`w-full px-6 py-3 rounded-lg font-medium transition-colors ${crawling || maxPages === ''
                ? 'bg-gray-300 cursor-not-allowed text-gray-600'
                : 'bg-blue-600 hover:bg-blue-700 text-white'
                }`}
            >
              {crawling ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin h-5 w-5 mr-2" viewBox="0 0 24 24">
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                      fill="none"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  Discovering Pages...
                </span>
              ) : (
                '🔍 Start Crawling'
              )}
            </button>
          </div>
        </div>
      )}

      {/* Step 2: Select Pages */}
      {currentStep === 2 && (
        <div className={`${isDarkMode ? 'bg-gray-800 text-gray-100' : 'bg-white'} p-6 rounded-lg shadow-md`}>
          <h2 className="text-2xl font-bold mb-4">Step 2: Select Pages for Analysis</h2>
          <p className={`${isDarkMode ? 'text-gray-300' : 'text-gray-600'} mb-6`}>
            Choose which pages you want to analyze. Each page costs 15 credits.
          </p>

          <div className="mb-4 flex justify-between items-center">
            <button
              onClick={handleSelectAll}
              className={`px-1 sm:px-4 text-sm sm:text-base py-2 rounded-lg transition-colors ${isDarkMode
                ? 'bg-gray-700 hover:bg-gray-600 text-gray-100'
                : 'bg-gray-200 hover:bg-gray-300'
                }`}
            >
              {selectedPageIds.length === pages.length ? '❌ Deselect All' : '✅ Select All'}
            </button>
            <span className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              {selectedPageIds.length} of {pages.length} pages selected
            </span>
          </div>

          {/* Quick search/add specific endpoint */}
          <form className="mb-4 grid gap-3 lg:grid-cols-[1.5fr_auto] items-end">
            <div>
              <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                Search or add a specific endpoint
              </label>
              <div className="flex flex-col max-w-4xl  sm:flex-row gap-2">
                <div className={`flex-1 flex items-center rounded-lg border overflow-hidden ${isDarkMode ? 'border-gray-400 bg-gray-700' : 'border-gray-300'}`}>
                  <span className={`px-3 text-sm whitespace-nowrap ${isDarkMode ? 'bg-gray-600 text-gray-200' : 'bg-gray-100 text-gray-700'}`}>
                    {baseDomain || 'https://yourdomain.com'}
                  </span>
                  <input
                    type="text"
                    value={customPath}
                    onChange={(e) => setCustomPath(e.target.value)}
                    placeholder="/blog/my-article"
                    className={`flex-1 px-3 py-2 text-sm outline-none ${isDarkMode
                      ? 'bg-gray-700 text-gray-100 placeholder-gray-400'
                      : 'bg-white text-gray-900 placeholder-gray-500'
                      }`}
                  />
                </div>
                {/* <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-semibold"
                >
                  Add & Select
                </button> */}
              </div>
              <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Enter the path to a page (e.g., /pricing). It will be appended to your domain and added to the list for selection.
              </p>
            </div>
          </form>

          <div className={`border max-h-96 overflow-y-auto rounded-lg p-4 space-y-2 ${isDarkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200'
            }`}>
            {filteredPages.length === 0 && (
              <div className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>No pages match that search.</div>
            )}
            {filteredPages.map((page) => (
              <div
                key={page.id}
                className={`flex items-center p-3 rounded-lg cursor-pointer transition-colors ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'
                  }`}
                onClick={() => handleTogglePage(page.id)}
              >
                <input
                  type="checkbox"
                  checked={selectedPageIds.includes(page.id)}
                  onChange={() => { }}
                  className="mr-3 w-5 h-5"
                />
                <span className={`text-sm break-all ${isDarkMode ? 'text-gray-100' : 'text-gray-700'}`}>{page.url}</span>
              </div>
            ))}
          </div>

          <div className="mt-6 flex gap-4">
            <button
              onClick={() => setCurrentStep(1)}
              className={`px-2 sm:px-6 py-1 sm:px-4 md:px-6 rounded-lg transition-colors ${isDarkMode
                ? 'bg-gray-700 hover:bg-gray-600 text-gray-100'
                : 'bg-gray-200 hover:bg-gray-300'
                }`}
            >
              ← Back
            </button>
            <button
              onClick={handleConfirmSelection}
              disabled={selectedPageIds.length === 0}
              className={`flex-1 px-2 sm:px-4 md:px-6 py-1 sm:py-3 rounded-lg text-sm sm:text-base font-medium transition-colors ${selectedPageIds.length === 0
                ? 'bg-gray-300 text-black cursor-not-allowed'
                : isDarkMode
                  ? 'bg-blue-600 hover:bg-blue-700 text-white'
                  : 'bg-blue-600 hover:bg-blue-700 text-white'
                }`}
            >
              Continue to Analysis →
            </button>
          </div>
        </div>
      )}

      {/* Step 3: Configure & Run Analysis */}
      {currentStep === 3 && (
        <div className="bg-white p-6 rounded-lg shadow-md">
          <h2 className="text-2xl font-bold mb-4">Step 3: Run SEO Analysis</h2>
          <p className="text-gray-600 mb-6">
            Configure your SEO analysis parameters and start the analysis.
          </p>

          <div className="space-y-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Target Keyword (Optional)
              </label>
              <input
                type="text"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="e.g., digital marketing services"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Location
                </label>
                <select
                  value={location_}
                  onChange={(e) => setLocation_(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="US">United States</option>
                  <option value="UK">United Kingdom</option>
                  <option value="CA">Canada</option>
                  <option value="AU">Australia</option>
                  <option value="IN">India</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Language
                </label>
                <select
                  value={language}
                  onChange={(e) => setLanguage(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="en">English</option>
                  <option value="es">Spanish</option>
                  <option value="fr">French</option>
                  <option value="de">German</option>
                  <option value="hi">Hindi</option>
                </select>
              </div>
            </div>
          </div>

          {/* User Credits Display */}
          {userCredits !== null && (
            <div className={`p-4 rounded-lg mb-4 ${userCredits === 0
              ? 'bg-red-50 border border-red-200'
              : userCredits < calculateCost()
                ? 'bg-amber-50 border border-amber-200'
                : 'bg-green-50 border border-green-200'
              }`}>
              <div className="flex justify-between items-center">
                <span className="font-medium">Your Available Credits:</span>
                <span className={`text-2xl font-bold ${userCredits === 0
                  ? 'text-red-600'
                  : userCredits < calculateCost()
                    ? 'text-amber-600'
                    : 'text-green-600'
                  }`}>
                  {userCredits}
                </span>
              </div>
              {userCredits === 0 && (
                <p className="text-sm text-red-600 mt-2">
                  {isMockDomain
                    ? "💡 Note: You have 0 credits, but you can still analyze this demo domain for free."
                    : "⚠️ You have zero credits. Please purchase a plan to run analysis."}
                </p>
              )}
              {userCredits > 0 && userCredits < calculateCost() && (
                <p className="text-sm text-amber-600 mt-2">
                  {isMockDomain
                    ? "💡 Note: You don't have enough credits, but you can still analyze this demo domain for free."
                    : `⚠️ Not enough credits for this analysis. Required: ${calculateCost()}`}
                </p>
              )}
            </div>
          )}

          {/* Cost Breakdown */}
          <div className="bg-blue-50 p-4 rounded-lg mb-6">
            <h3 className="font-semibold mb-2">Credit Cost Breakdown</h3>
            <div className="space-y-1 text-sm">
              <div className="flex justify-between">
                <span>Domain-level analysis:</span>
                <span className="font-semibold">{isMockDomain ? '0 credits (Free Demo)' : '24 credits'}</span>
              </div>
              <div className="flex justify-between">
                <span>Page-level analysis ({selectedPageIds.length} pages × 20):</span>
                <span className="font-semibold">{isMockDomain ? '0 credits (Free Demo)' : `${selectedPageIds.length * 20} credits`}</span>
              </div>
              <div className="border-t border-blue-200 mt-2 pt-2 flex justify-between text-lg">
                <span className="font-bold">Total Cost:</span>
                <span className="font-bold text-blue-600">{calculateCost()} credits</span>
              </div>
            </div>
          </div>

          {showPlanBanner && (
            <div className="mb-6 bg-amber-50 border border-amber-200 text-amber-900 rounded-lg p-4">
              <div className="text-lg font-semibold mb-1">You need a plan to run this analysis</div>
              <p className="text-sm mb-3">Not enough credits or no active plan. Please choose a plan to continue.</p>
              <button
                onClick={() => navigate('/dashboard/pricing')}
                className="inline-flex items-center gap-2 px-4 py-2 bg-amber-500 text-amber-900 font-semibold rounded-lg hover:bg-amber-400 transition"
              >
                View Pricing Plans
              </button>
            </div>
          )}

          <div className="flex gap-4">
            <button
              onClick={() => setCurrentStep(2)}
              className="px-2 py-1 sm:px-4 md:px-6 bg-gray-200 hover:bg-gray-300 rounded-lg"
            >
              ← Back
            </button>
            <button
              onClick={handleRunAnalysis}
              disabled={analyzing || (!hasSubscription && userCredits === 0 && !trialEligible && !isMockDomain)}
              className={`flex-1 px-2 sm:px-4 md:px-6 py-1 sm:py-3 rounded-lg font-medium ${analyzing || (!hasSubscription && userCredits === 0 && !trialEligible && !isMockDomain)
                ? 'bg-gray-300 cursor-not-allowed text-gray-600'
                : 'bg-green-600 hover:bg-green-700 text-white'
                }`}
            >
              {analyzing ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin h-5 w-5 mr-2" viewBox="0 0 24 24">
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                      fill="none"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  Analyzing... This may take a few minutes
                </span>
              ) : !hasSubscription && trialEligible && !trialUsed ? (
                '🚀 Start Free Analysis'
              ) : (
                `🚀 Run Analysis (${calculateCost()} Credits)`
              )}
            </button>
          </div>
        </div>
      )}

      {/* Step 4: Results Summary */}
      {currentStep === 4 && analysisResult && (
        <div className="bg-white p-6 rounded-lg shadow-md">
          <h2 className="text-2xl font-bold mb-4 text-green-600">✅ Analysis Complete!</h2>

          <div className="grid grid-cols-3 gap-4 mb-6">
            <div className="p-4 bg-blue-50 rounded-lg">
              <p className="text-sm text-gray-600">Credits Used</p>
              <p className="text-3xl font-bold text-blue-600">
                {analysisResult.credits_deducted}
              </p>
            </div>
            <div className="p-4 bg-green-50 rounded-lg">
              <p className="text-sm text-gray-600">Credits Remaining</p>
              <p className="text-3xl font-bold text-green-600">
                {analysisResult.remaining_credits}
              </p>
            </div>
            <div className="p-4 bg-purple-50 rounded-lg">
              <p className="text-sm text-gray-600">Run ID</p>
              <p className="text-sm font-mono text-purple-600 truncate">
                {analysisResult.run_id}
              </p>
            </div>
          </div>

          <button
            onClick={() => {
              // Navigate to SEO results page with project ID and run ID
              navigate(`/dashboard/projects/${projectId}/seo-results?run_id=${analysisResult.run_id}`);
            }}
            className="w-full px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium"
          >
            View Detailed Results →
          </button>
        </div>
      )}

    </div>
  );
};

export default WebsiteSeoAnalyzer;
